import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { HttpService } from './http-service.service';
import { IBranchExchangeModel } from '../logic/branch/models/branch-exchange.model';
import { IExchangeModel } from '../logic/exchanges/model/exchange.model';
import { IBranchModel } from '../logic/branch/models/branch.model';
import { ICurrencyModel } from '../logic/currency/model/currency.model';
import {
  ApiData,
  ApiPaginationResponse,
  ApiResponse,
} from '../common/api-response.types';

export interface IExchangeHistoryItem {
  buyPrice: string;
  sellPrice: string;
  date: string;
  createdAt: string;
}

interface ExchangeRateApiEntity {
  id: number;
  branchId?: number;
  currencyId?: number;
  buy?: number;
  sell?: number;
  buyPrice?: string | number;
  sellPrice?: string | number;
  lastUpdated?: string;
  deletedAt?: string | null;
  currency?: Partial<ICurrencyModel>;
}

@Injectable({
  providedIn: 'root',
})
export class ExchangeService {
  private readonly exchangeEndpoint = '/branch';

  constructor(private readonly httpService: HttpService) {}

  public getBranchExchangeRates(
    branchId: number,
    branchName?: string,
  ): Observable<IBranchExchangeModel> {
    return this.httpService
      .get<
        ApiResponse<ExchangeRateApiEntity>
      >(`${this.exchangeEndpoint}/${branchId}/exchange-rates`)
      .pipe(
        map((response: ApiResponse<ExchangeRateApiEntity>) => {
          const exchangeRates = this.mapApiResponse(response);
          const parsedBranchName =
            branchName ?? this.resolveBranchNameFromSession(branchId);

          return {
            id: branchId,
            name: parsedBranchName,
            exchange: exchangeRates,
          };
        }),
      );
  }

  public getCurrencies(): Observable<ICurrencyModel[]> {
    return this.httpService
      .get<ApiResponse<ICurrencyModel>>('/currencies')
      .pipe(
        map((response) => {
          return this.extractDataArray(response.data);
        }),
      );
  }

  public getExchangeHistory(
    branchId: number,
    currencyId: number,
    startDate: string,
    endDate: string,
  ): Observable<IExchangeHistoryItem[]> {
    return this.httpService
      .get<
        ApiResponse<IExchangeHistoryItem>
      >(`/exchange-history?branchId=${branchId}&currencyId=${currencyId}&startDate=${startDate}&endDate=${endDate}`)
      .pipe(
        map((response) => {
          return this.extractDataArray(response.data);
        }),
      );
  }

  private mapApiResponse(
    response: ApiResponse<ExchangeRateApiEntity>,
  ): IExchangeModel[] {
    if (!response?.data) {
      return [];
    }

    const payload = this.extractDataArray(response.data);
    return payload.map((rate) => this.mapToExchangeModel(rate));
  }

  private extractDataArray<T>(data: ApiData<T> | undefined): T[] {
    if (!data) {
      return [];
    }

    if (Array.isArray(data)) {
      return data;
    }

    if (this.isPaginationResponse(data)) {
      if (Array.isArray(data.items)) {
        return data.items;
      }
      return [];
    }

    return [data as T];
  }

  private mapToExchangeModel(rate: ExchangeRateApiEntity): IExchangeModel {
    return {
      id: rate.id,
      buy: this.parseNumber(rate.buyPrice ?? rate.buy),
      sell: this.parseNumber(rate.sellPrice ?? rate.sell),
      active: !rate.deletedAt,
      updateAt: rate.lastUpdated ?? '',
      currency: this.mapCurrency(rate.currency, rate.currencyId),
    };
  }

  private mapCurrency(
    currency: Partial<ICurrencyModel> | undefined,
    currencyId?: number,
  ): Partial<ICurrencyModel> {
    if (currency) {
      return {
        id: currency.id ?? currencyId ?? 0,
        name: currency.name ?? '',
        fullName: currency.fullName ?? '',
        iconPath: currency.iconPath || currency.logoUrl || '',
        logoUrl: currency.logoUrl || currency.iconPath || '',
        code: currency.code ?? '',
        order: currency.order ?? 0,
      };
    }

    return {
      id: currencyId ?? 0,
      name: '',
      fullName: '',
      iconPath: '',
    };
  }

  private parseNumber(value: string | number | undefined): number {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  }

  private resolveBranchNameFromSession(branchId: number): string {
    try {
      const stored = localStorage.getItem('branches');
      if (!stored) {
        return '';
      }

      const branches: Array<Partial<IBranchModel> & { branchName?: string }> =
        JSON.parse(stored);
      const branch = branches.find((item) => item?.id === branchId);

      return branch?.name ?? branch?.branchName ?? '';
    } catch {
      return '';
    }
  }

  private isPaginationResponse<T>(
    data: ApiData<T>,
  ): data is ApiPaginationResponse<T> {
    if (data === null || typeof data !== 'object') {
      return false;
    }

    return Object.hasOwn(data as Record<string, unknown>, 'items');
  }
}
